import { FC, useEffect, useMemo, useState } from "react"
import AlertSoundFrequencyOptionsList from "./AlertSoundFrequencyOptionsList"
import AlertSoundOptionsList from "./AlertSoundOptionsList"
import AlertSetting from "@/components/AlertSetting"
import Actions from "@/components/AlertSetting/Actions"
import {
   AlertSoundFrequency,
   noSoundFile,
   SoundFile,
} from "@/components/AlertSoundSettings/files"
import Dialog from "@/components/shared/Dialog"
import { IconColor } from "@/components/shared/Icon"
import { TestId } from "@/constants/globals"
import useDialog from "@/hooks/useDialog"
import "./index.scss"

interface AlertSoundSettingsProps {
   alarmName: string
   soundFile: SoundFile
   soundFrequency: AlertSoundFrequency
   onSave: (soundFile: SoundFile, soundFrequency: AlertSoundFrequency) => void
}

const AlertSoundSettings: FC<AlertSoundSettingsProps> = ({
   alarmName,
   soundFile,
   soundFrequency,
   onSave,
}) => {
   const [formSoundFile, setFormSoundFile] = useState<SoundFile>(soundFile)
   const [formSoundFrequency, setFormSoundFrequency] =
      useState<AlertSoundFrequency>(soundFrequency)
   const [isEditing, setIsEditing] = useState(false)
   const { isOpen: isDialogOpen, openDialog, closeDialog } = useDialog()

   useEffect(() => {
      setFormSoundFile(soundFile)
      setFormSoundFrequency(soundFrequency)
   }, [soundFile, soundFrequency])

   const isFormChanged = useMemo(() => {
      return (
         formSoundFile.id !== soundFile.id ||
         formSoundFrequency.id !== soundFrequency.id
      )
   }, [formSoundFile, soundFile, formSoundFrequency, soundFrequency])

   const isMutingSound =
      formSoundFile.id === noSoundFile.id && soundFile.id !== noSoundFile.id

   const handleCancel = () => {
      setFormSoundFile(soundFile)
      setFormSoundFrequency(soundFrequency)
      setIsEditing(false)
   }

   const commitSave = () => {
      onSave(formSoundFile, formSoundFrequency)
      setIsEditing(false)
      closeDialog()
   }

   const handleSaveClick = () => {
      if (isMutingSound) {
         openDialog()
         return
      }
      commitSave()
   }

   return (
      <AlertSetting
         iconColor={IconColor.HONOLULU_BLUE}
         iconName="sound"
         title="Sound"
      >
         <div className="alert-sound-setting">
            <div className="alert-sound-setting__group">
               <div className="alert-sound-setting__title">
                  <span>Soundclip</span>
                  <Actions
                     isDisabled={!isFormChanged}
                     isEditing={isEditing}
                     onCancel={handleCancel}
                     onEdit={() => setIsEditing(true)}
                     onSave={handleSaveClick}
                  />
               </div>
               <table>
                  <tbody>
                     <AlertSoundOptionsList
                        isEditing={isEditing}
                        soundFile={soundFile}
                        formSoundFile={formSoundFile}
                        setFormSoundFile={setFormSoundFile}
                     />
                  </tbody>
               </table>
            </div>
            <div className="alert-sound-setting__group">
               <div className="alert-sound-setting__title">
                  <span>Frequency</span>
               </div>
               <table>
                  <tbody>
                     <AlertSoundFrequencyOptionsList
                        isEditing={isEditing}
                        soundFrequency={soundFrequency}
                        formSoundFrequency={formSoundFrequency}
                        setFormSoundFrequency={setFormSoundFrequency}
                     />
                  </tbody>
               </table>
            </div>
         </div>
         {isDialogOpen && (
            <Dialog
               open={isDialogOpen}
               title={`Are you sure you want to mute the sound for ${alarmName} for the whole facility? The alarm will still be visible on the alarm header`}
               titleTestId={TestId.MUTE_ALARM_DIALOG_TITLE}
               buttonTitle="Confirm"
               handleClose={closeDialog}
               handleButtonClick={commitSave}
            />
         )}
      </AlertSetting>
   )
}

export default AlertSoundSettings
